import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Text,
  Title,
  Button,
  Group,
  Modal,
  Loader,
  Center,
  Stack,
  Paper,
  ThemeIcon,
  Flex,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { ChatContainer } from "../ChatContainer";
import { Header } from "../Header";
import {
  NOT_ACCEPTING_CANDIDATES_MESSAGE,
  PausedScreen,
} from "../PausedScreen";
import { apiService } from "../../services/api.service";
import { INTERVIEW_STATIC_TEXTS } from "../../constants/static-translations";
import { useTranslation } from "../../hooks";
import { isLocationPausedError } from "../../utils";
import {
  ResumeSessionResponse,
  ContinueResumeResponse,
  RestartResumeResponse,
  SessionMessage,
  MessageRole,
  Message,
  InterviewPhase,
  InterviewQuestionInputType,
  InputOption,
  RequestStates,
  ResponseFormat,
} from "../../types";

/** All static UI strings on the resume page, used for batch pre-translation. */
const RESUME_PAGE_STRINGS = [
  "Welcome back",
  "Continue Where I Left Off",
  "Start Over",
  "Start Over?",
  "Are you sure you want to start over? Your previous progress will be lost and a new interview session will begin.",
  "Cancel",
  "Yes, Start Over",
  "Loading your session...",
  "Something Went Wrong",
  "Interview Already Completed",
  "This interview session has already been completed. Thank you for your participation!",
  "Session Expired",
  "This resume link has expired. Sessions are available for 24 hours after they are created.",
  "Error",
  "Failed to continue session. Please try again.",
  "Failed to restart session. Please try again.",
  "You have a saved interview session",
  "with",
  "for the",
  "position",
  "How would you like to proceed?",
];

/** Everything the resume flow translates: landing-page strings + chat statics. */
const RESUME_PRELOAD_TEXTS = [
  ...RESUME_PAGE_STRINGS,
  ...INTERVIEW_STATIC_TEXTS,
];

type PageState =
  | "loading"
  | "choice"
  | "completed"
  | "expired"
  | "error"
  | "notAccepting"
  | "interview";

export function ResumePage() {
  const { resumeToken } = useParams<{ resumeToken: string }>();

  const [pageState, setPageState] = useState<PageState>("loading");
  const [sessionData, setSessionData] = useState<ResumeSessionResponse | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Interview mode state
  const [activeSessionId, setActiveSessionId] = useState<string>("");
  const [activeLocationId, setActiveLocationId] = useState<string>("");
  const [resumeMessages, setResumeMessages] = useState<Message[]>([]);
  const [resumePhase, setResumePhase] = useState<InterviewPhase | undefined>();
  const [pusherChannel, setPusherChannel] = useState<string>("");
  const [resumeCanEditPreviousResponse, setResumeCanEditPreviousResponse] =
    useState(false);
  // AS-1564: progress fields lifted from ChatContainer so the Header renders
  // the progress bar on resume. Set immediately from the rehydrated last
  // assistant message in useInterview.
  const [currentQuestionNumber, setCurrentQuestionNumber] = useState<
    number | undefined
  >(undefined);
  const [totalQuestions, setTotalQuestions] = useState<number | undefined>(
    undefined,
  );
  const handleProgressUpdate = useCallback(
    (current: number | undefined, total: number | undefined) => {
      setCurrentQuestionNumber(current);
      setTotalQuestions(total);
    },
    [],
  );

  // Restart confirmation modal
  const [restartModalOpen, setRestartModalOpen] = useState(false);
  const [isRestarting, setIsRestarting] = useState(false);
  const [isContinuing, setIsContinuing] = useState(false);

  const { t, preloadTranslations } = useTranslation(
    sessionData?.language ?? null,
  );

  // Pre-load translations once session data (and language) is available
  useEffect(() => {
    if (sessionData?.language) {
      preloadTranslations(RESUME_PRELOAD_TEXTS);
    }
  }, [sessionData?.language, preloadTranslations]);

  useEffect(() => {
    if (!resumeToken) {
      setErrorMessage("Invalid resume link. No token provided.");
      setPageState("error");
      return;
    }

    const fetchSession = async () => {
      try {
        const data = await apiService.getResumeSession(resumeToken);
        setSessionData(data);

        switch (data.sessionStatus) {
          case "completed":
            setPageState("completed");
            break;
          case "expired":
            setPageState("expired");
            break;
          case "active":
            setPageState("choice");
            break;
          default:
            setPageState("error");
            setErrorMessage("Unknown session status.");
        }
      } catch {
        setErrorMessage(
          "Unable to load your session. The link may be invalid or expired.",
        );
        setPageState("error");
      }
    };

    fetchSession();
  }, [resumeToken]);

  const handleContinue = useCallback(async () => {
    if (!resumeToken || !sessionData) return;

    setIsContinuing(true);
    try {
      const continueResponse: ContinueResumeResponse =
        await apiService.continueResumeSession(resumeToken);

      // AS-1935: the mount-time preload runs before any session token exists,
      // so /translation/static rejects it (401) and the cache stays empty.
      // Continue just minted a token — re-run the preload so the chat UI
      // renders translated. Fire-and-forget; preloadTranslations never throws.
      // continueResumeSession stores the token before resolving; that
      // invariant is pinned by api.service.test.ts.
      void preloadTranslations(RESUME_PRELOAD_TEXTS);

      // Load existing messages
      const messages = await apiService.getSessionMessages(
        continueResponse.sessionId,
      );

      // Convert SessionMessage[] to Message[]. Voice metadata
      // (uploadUrl/audioUrl/responseFormat/canSkip/recordingDurationSeconds)
      // is regenerated by the API on the last assistant message of voice
      // questions; pass it through so the recorder renders on resume.
      const convertedMessages: Message[] = messages.map(
        (m: SessionMessage) => ({
          id: m.id,
          role: m.role as MessageRole,
          content: m.content,
          messageOrder: m.messageOrder,
          questionId: m.interviewQuestionId ?? undefined,
          timestamp: new Date(m.createdAt),
          status: RequestStates.Success,
          inputType: m.inputType as InterviewQuestionInputType | undefined,
          options: m.options as InputOption[] | undefined,
          uploadUrl: m.uploadUrl,
          audioUrl: m.audioUrl,
          responseFormat: m.responseFormat as ResponseFormat | undefined,
          answerResponseFormat: m.answerResponseFormat,
          audioS3Key: m.audioS3Key,
          canSkip: m.canSkip,
          recordingDurationSeconds: m.recordingDurationSeconds,
          // AS-1564 — progress fields on the last assistant message let
          // useInterview rehydrate the bar immediately on resume.
          currentQuestionNumber: m.currentQuestionNumber,
          totalQuestions: m.totalQuestions,
        }),
      );

      setActiveSessionId(continueResponse.sessionId);
      setActiveLocationId(sessionData.screenerLocationId);
      setResumeMessages(convertedMessages);
      setResumePhase((continueResponse.phase as InterviewPhase) ?? undefined);
      setPusherChannel(continueResponse.pusherChannel);
      setResumeCanEditPreviousResponse(
        continueResponse.canEditPreviousResponse ?? false,
      );
      setPageState("interview");
    } catch {
      notifications.show({
        title: t("Error"),
        message: t("Failed to continue session. Please try again."),
        color: "red",
      });
    } finally {
      setIsContinuing(false);
    }
  }, [resumeToken, sessionData, t, preloadTranslations]);

  const handleRestart = useCallback(async () => {
    if (!resumeToken || !sessionData) return;

    setIsRestarting(true);
    try {
      const restartResponse: RestartResumeResponse =
        await apiService.restartResumeSession(resumeToken);

      // AS-1935: same as handleContinue — re-run the preload now that
      // restart minted a session token.
      void preloadTranslations(RESUME_PRELOAD_TEXTS);

      setActiveSessionId(restartResponse.sessionId);
      setActiveLocationId(sessionData.screenerLocationId);
      setResumeMessages([]);
      setPusherChannel(restartResponse.pusherChannel);
      setRestartModalOpen(false);
      setPageState("interview");
    } catch (error) {
      // A paused location refuses the fresh session (409 location_paused).
      // Retrying can't help, so show the not-accepting screen instead of the
      // generic "try again" toast.
      if (isLocationPausedError(error)) {
        setRestartModalOpen(false);
        setPageState("notAccepting");
        return;
      }
      notifications.show({
        title: t("Error"),
        message: t("Failed to restart session. Please try again."),
        color: "red",
      });
    } finally {
      setIsRestarting(false);
    }
  }, [resumeToken, sessionData, t, preloadTranslations]);

  // Loading state
  if (pageState === "loading") {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text c="dimmed">{t("Loading your session...")}</Text>
        </Stack>
      </Center>
    );
  }

  // Error state
  if (pageState === "error") {
    return (
      <Container size="sm" py="xl">
        <Paper p="xl" radius="md" withBorder>
          <Stack align="center" gap="md">
            <ThemeIcon size="xl" radius="xl" color="red" variant="light">
              !
            </ThemeIcon>
            <Title order={3}>{t("Something Went Wrong")}</Title>
            <Text c="dimmed" ta="center">
              {errorMessage}
            </Text>
          </Stack>
        </Paper>
      </Container>
    );
  }

  // Completed state
  if (pageState === "completed") {
    return (
      <Container size="sm" py="xl">
        <Paper p="xl" radius="md" withBorder>
          <Stack align="center" gap="md">
            <ThemeIcon size="xl" radius="xl" color="green" variant="light">
              &#10003;
            </ThemeIcon>
            <Title order={3}>{t("Interview Already Completed")}</Title>
            <Text c="dimmed" ta="center">
              {t(
                "This interview session has already been completed. Thank you for your participation!",
              )}
            </Text>
          </Stack>
        </Paper>
      </Container>
    );
  }

  // Expired state
  if (pageState === "expired") {
    return (
      <Container size="sm" py="xl">
        <Paper p="xl" radius="md" withBorder>
          <Stack align="center" gap="md">
            <ThemeIcon size="xl" radius="xl" color="orange" variant="light">
              &#8987;
            </ThemeIcon>
            <Title order={3}>{t("Session Expired")}</Title>
            <Text c="dimmed" ta="center">
              {t(
                "This resume link has expired. Sessions are available for 24 hours after they are created.",
              )}
            </Text>
          </Stack>
        </Paper>
      </Container>
    );
  }

  // Paused location — a restart can't mint a session (parity with the start
  // flow's paused screen).
  if (pageState === "notAccepting") {
    return (
      <Flex direction="column" mih="100dvh">
        <PausedScreen
          data-testid="resume-not-accepting"
          message={t(NOT_ACCEPTING_CANDIDATES_MESSAGE)}
        />
      </Flex>
    );
  }

  // Interview state (active chat)
  if (pageState === "interview") {
    return (
      <div className="interview-page">
        <Header
          company={sessionData?.company ?? "Your Company"}
          title={sessionData?.jobTitle ?? "AI Interview"}
          sessionId={activeSessionId}
          candidateEmail={sessionData?.candidateEmail ?? undefined}
          currentQuestionNumber={currentQuestionNumber}
          totalQuestions={totalQuestions}
          translate={t}
        />
        <ChatContainer
          interviewLocationId={activeLocationId}
          company={sessionData?.company ?? "Your Company"}
          resumeSessionId={activeSessionId}
          resumeMessages={resumeMessages}
          resumePusherChannel={pusherChannel}
          resumePhase={resumePhase}
          resumeCanEditPreviousResponse={resumeCanEditPreviousResponse}
          onProgressUpdate={handleProgressUpdate}
          translate={t}
        />
      </div>
    );
  }

  // Choice state (welcome back screen)
  const candidateName = sessionData?.candidateName;

  return (
    <>
      <Container size="sm" py="xl">
        <Paper p="xl" radius="md" withBorder>
          <Stack align="center" gap="lg">
            <Title order={2}>
              {t("Welcome back")}
              {candidateName ? `, ${candidateName}` : ""}!
            </Title>
            <Text c="dimmed" ta="center">
              {t("You have a saved interview session")}
              {sessionData?.company
                ? ` ${t("with")} ${sessionData.company}`
                : ""}
              {sessionData?.jobTitle
                ? ` ${t("for the")} ${sessionData.jobTitle} ${t("position")}`
                : ""}
              . {t("How would you like to proceed?")}
            </Text>

            <Group mt="md">
              <Button
                size="lg"
                onClick={handleContinue}
                loading={isContinuing}
                disabled={isRestarting}
              >
                {t("Continue Where I Left Off")}
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => setRestartModalOpen(true)}
                disabled={isContinuing}
              >
                {t("Start Over")}
              </Button>
            </Group>
          </Stack>
        </Paper>
      </Container>

      <Modal
        opened={restartModalOpen}
        onClose={() => setRestartModalOpen(false)}
        title={t("Start Over?")}
        centered
      >
        <Stack gap="md">
          <Text>
            {t(
              "Are you sure you want to start over? Your previous progress will be lost and a new interview session will begin.",
            )}
          </Text>
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setRestartModalOpen(false)}
              disabled={isRestarting}
            >
              {t("Cancel")}
            </Button>
            <Button color="red" onClick={handleRestart} loading={isRestarting}>
              {t("Yes, Start Over")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
